import { ErrorInfo } from 'react';

interface ErrorReport {
  error: Error;
  errorInfo?: ErrorInfo;
  timestamp: number;
  userAgent: string;
  url: string;
  userId?: string;
  sessionId: string;
  componentStack?: string;
  additionalContext?: Record<string, any>;
}

interface ErrorTrackingConfig {
  enabled: boolean;
  endpoint?: string;
  maxErrors: number;
  excludePatterns: RegExp[];
  onError?: (errorReport: ErrorReport) => void;
}

class ErrorTracker {
  private config: ErrorTrackingConfig;
  private errorQueue: ErrorReport[] = [];
  private sessionId: string;

  constructor(config: Partial<ErrorTrackingConfig> = {}) {
    this.config = {
      enabled: process.env.NODE_ENV === 'production',
      maxErrors: 10,
      excludePatterns: [
        /Network Error/i,
        /Loading chunk \d+ failed/i,
        /Script error/i,
      ],
      ...config,
    };

    this.sessionId = this.generateSessionId();
    this.setupGlobalErrorHandlers();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupGlobalErrorHandlers(): void {
    if (typeof window === 'undefined' || !this.config.enabled) {
      return;
    }

    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      this.captureError(new Error(event.message), undefined, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(
        new Error(`Unhandled Promise Rejection: ${event.reason}`),
        undefined,
        {
          reason: event.reason,
        }
      );
    });
  }

  public captureError(
    error: Error,
    errorInfo?: ErrorInfo,
    additionalContext?: Record<string, any>
  ): void {
    if (!this.config.enabled || this.shouldExcludeError(error)) {
      return;
    }

    const errorReport: ErrorReport = {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      } as Error,
      errorInfo,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      sessionId: this.sessionId,
      userId: undefined,
      componentStack: errorInfo?.componentStack || undefined,
      additionalContext,
    };

    this.addToQueue(errorReport);
    this.processQueue();

    // Call custom error handler if provided
    if (this.config.onError) {
      this.config.onError(errorReport);
    }
  }

  private shouldExcludeError(error: Error): boolean {
    return this.config.excludePatterns.some(pattern =>
      pattern.test(error.message)
    );
  }

  private addToQueue(errorReport: ErrorReport): void {
    this.errorQueue.push(errorReport);

    // Limit queue size
    if (this.errorQueue.length > this.config.maxErrors) {
      this.errorQueue.shift();
    }
  }

  private async processQueue(): Promise<void> {
    if (!this.config.endpoint || this.errorQueue.length === 0) {
      return;
    }

    const errorsToSend = [...this.errorQueue];
    this.errorQueue = [];

    try {
      await fetch(this.config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          errors: errorsToSend,
          metadata: {
            timestamp: Date.now(),
            sessionId: this.sessionId,
            userAgent: navigator.userAgent,
            url: window.location.href,
          },
        }),
      });
    } catch (fetchError) {
      // If sending fails, add errors back to queue
      this.errorQueue.unshift(...errorsToSend);
      console.warn('Failed to send error reports:', fetchError);
    }
  }

  public getErrorHistory(): ErrorReport[] {
    return [...this.errorQueue];
  }

  public clearErrorHistory(): void {
    this.errorQueue = [];
  }

  public updateConfig(newConfig: Partial<ErrorTrackingConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// Create a singleton instance
export const errorTracker = new ErrorTracker();

// Hook for React Error Boundaries
export function useErrorTracking() {
  const captureError = (error: Error, errorInfo?: ErrorInfo) => {
    errorTracker.captureError(error, errorInfo);
  };

  const captureException = (error: Error, context?: Record<string, any>) => {
    errorTracker.captureError(error, undefined, context);
  };

  return {
    captureError,
    captureException,
    getErrorHistory: () => errorTracker.getErrorHistory(),
    clearErrorHistory: () => errorTracker.clearErrorHistory(),
  };
}

// Initialize error tracking with default config
export function initializeErrorTracking(config?: Partial<ErrorTrackingConfig>) {
  if (config) {
    errorTracker.updateConfig(config);
  }
  return errorTracker;
}

// Utility function to capture user feedback with errors
export function captureUserFeedback(
  feedback: string,
  email?: string,
  additionalData?: Record<string, any>
) {
  errorTracker.captureError(
    new Error('User Feedback'),
    undefined,
    {
      feedback,
      email,
      ...additionalData,
    }
  );
}

export default errorTracker;